import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useDebounceFn } from '@vueuse/core'

/**
 * Default section configurations for the DataTab
 * Each section has an id (matching the DOM element), translation key, and optional icon
 */
const DEFAULT_SECTIONS = [
  {
    id: 'section-main',
    translationKey: 'messages.detail.main.title',
    icon: 'pi-file-edit',
  },
  {
    id: 'section-maps',
    translationKey: 'messages.detail.map.title',
    icon: 'pi-map-marker',
  },
  {
    id: 'section-ehr-general',
    translationKey: 'messages.detail.ehrGeneral.title',
    icon: 'pi-home',
  },
  {
    id: 'section-building-parts',
    translationKey: 'messages.detail.ehrlivingRoom.title',
    icon: 'pi-th-large',
  },
  {
    id: 'section-documents',
    translationKey: 'messages.detail.documents.title',
    icon: 'pi-folder',
  },
  {
    id: 'section-transactions',
    translationKey: 'messages.markedtransactions.title',
    icon: 'pi-chart-line',
  },
]

/**
 * Smoothly scroll to a section by DOM id without registering any listeners.
 * Shared by the composable below and by one-off anchors (e.g. the RED-290
 * tightness badge) that only need the jump, not active-section tracking.
 *
 * @param {string} sectionId - The ID of the section to scroll to
 * @param {Object} [options]
 * @param {number} [options.offset=100] - Distance kept above the section
 * @param {Object} [options.container=null] - Scrolling component (its `$el` scrolls); window when null
 */
export function scrollToSectionId(sectionId, { offset = 100, container = null } = {}) {
  const element = document.getElementById(sectionId)

  if (!element) return

  if (!container) {
    // Fallback to window scroll if container not found
    const elementTop =
      element.getBoundingClientRect().top + window.scrollY - offset

    window.scrollTo({ top: elementTop, behavior: 'smooth' })
    return
  }

  const elementTop = element.offsetTop - offset

  container.$el.scrollTo({ top: elementTop, behavior: 'smooth' })
}

/**
 * Composable for managing scroll shortcuts with active section tracking
 * @param {Object} options - Configuration options
 * @param {Array} options.sections - Array of section configurations (defaults to DEFAULT_SECTIONS)
 * @param {Array} options.enabledSections - Array of section IDs to enable (defaults to all)
 * @param {Object} options.detailContainer - Reference to the detail container element
 * @param {number} options.offset - Offset from top when scrolling (defaults to 100)
 */
export function useScrollShortcuts(options = {}) {
  const {
    sections = DEFAULT_SECTIONS,
    enabledSections = null,
    detailContainer = null,
    offset = 100,
  } = options

  const activeSectionId = ref(null)
  let scrollHandler = null

  // Filter sections based on enabledSections
  const availableSections = computed(() => {
    if (!enabledSections) return sections
    return sections.filter(section => enabledSections.includes(section.id))
  })

  /**
   * Smoothly scroll to a section by ID
   * @param {string} sectionId - The ID of the section to scroll to
   */
  const scrollToSection = sectionId =>
    scrollToSectionId(sectionId, { offset, container: detailContainer })

  /**
   * Update active section based on scroll position
   * Always finds the section closest to the top of the viewport
   * Ensures a section is always active (never null)
   */
  const updateActiveSection = () => {
    let closestSection = null
    let closestDistance = Number.POSITIVE_INFINITY

    for (const section of availableSections.value) {
      const element = document.getElementById(section.id)
      if (element) {
        const rect = element.getBoundingClientRect()
        // Section is "closest" if its top is nearest to the offset point,
        // or if we're inside the section (top is above offset, bottom is below)
        const sectionTop = rect.top - offset
        const sectionBottom = rect.bottom - offset

        // If we're inside the section, it should be active
        if (sectionTop <= 0 && sectionBottom > 0) {
          closestSection = section.id
          break
        }

        // Otherwise find the section closest to the offset point
        const distance = Math.abs(sectionTop)
        if (distance < closestDistance) {
          closestDistance = distance
          closestSection = section.id
        }
      }
    }

    if (closestSection) {
      activeSectionId.value = closestSection
    }
  }

  /**
   * Setup scroll listener for tracking active section
   */
  const setup = () => {
    cleanup()

    // Debounce scroll handler for performance (16ms ≈ 60fps)
    const debouncedUpdate = useDebounceFn(updateActiveSection, 16)

    scrollHandler = () => debouncedUpdate()

    // Listen on window for scroll events
    window.addEventListener('scroll', scrollHandler, { passive: true })

    // Initialize active section immediately
    updateActiveSection()
  }

  /**
   * Cleanup scroll listener
   */
  const cleanup = () => {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler)
      scrollHandler = null
    }
  }

  /**
   * Refresh (useful when sections are dynamically added/removed)
   */
  const refresh = () => {
    cleanup()
    // Use setTimeout to allow DOM to update
    setTimeout(setup, 100)
  }

  onMounted(() => {
    // Delay setup to ensure DOM is ready
    setTimeout(setup, 200)
  })

  onBeforeUnmount(() => {
    cleanup()
  })

  // Re-setup when enabled sections change
  watch(
    () => enabledSections,
    () => {
      refresh()
    },
    { deep: true },
  )

  return {
    sections: availableSections,
    activeSectionId,
    scrollToSection,
    refresh,
    cleanup,
    DEFAULT_SECTIONS,
  }
}
